import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { smoothScrollTo } from '../utils/scrollTo';
import '../styles/Services.css';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    icon: '🌐',
    title: 'Website Development',
    description:
      'Full-stack web applications built with the latest technologies — React, Django, and more. From pixel-perfect UIs to robust backends, with SEO optimization baked in from day one.',
    tags: ['React', 'Django', 'REST APIs', 'SEO', 'Responsive'],
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    icon: '📦',
    title: 'ERP Customization',
    description:
      'Deep customization of ERPNext / Frappe to fit your exact business workflows — custom doctypes, reports, dashboards, print formats, and automation scripts.',
    tags: ['ERPNext', 'Frappe', 'Python', 'Custom Workflows'],
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  },
  {
    icon: '🔗',
    title: 'System Integration',
    description:
      'Seamless integrations between websites, third-party platforms, and ERP systems. Shopify ↔ ERPNext, payment gateways, shipping APIs, webhooks — all connected and automated.',
    tags: ['API Integration', 'Webhooks', 'Shopify', 'ERPNext', 'Automation'],
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
];

const Services = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(titleRef.current.children, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
        y: 60,
        autoAlpha: 0,
        stagger: 0.15,
        duration: 0.9,
        ease: 'power3.out',
      });

      gsap.from(cardsRef.current.children, {
        scrollTrigger: {
          trigger: cardsRef.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        y: 60,
        autoAlpha: 0,
        scale: 0.95,
        stagger: 0.12,
        duration: 0.7,
        ease: 'power3.out',
        clearProps: 'transform',
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleServiceClick = (serviceTitle) => {
    smoothScrollTo('contact', 1.1);

    let mapped = 'ERPNext & Frappe Customization';
    if (serviceTitle.includes('Website')) {
      mapped = 'Full-Stack Web App (React / Django / Python)';
    } else if (serviceTitle.includes('Integration')) {
      mapped = 'System Integration & APIs (Shopify / Tax)';
    }

    window.dispatchEvent(new CustomEvent('select-service', { detail: mapped }));
  };

  return (
    <section ref={sectionRef} className="services" id="services">
      <div className="services-container">
        <div ref={titleRef} className="services-header">
          <span className="services-label">What I Offer</span>
          <h2 className="services-title">Services</h2>
          <p className="services-subtitle">
            End-to-end solutions — from idea to deployment. Click any service to start an inquiry.
          </p>
        </div>

        <div ref={cardsRef} className="services-grid">
          {services.map((service, i) => (
            <div
              key={i}
              className="service-card"
              onClick={() => handleServiceClick(service.title)}
              onKeyDown={(e) => e.key === 'Enter' && handleServiceClick(service.title)}
              role="button"
              tabIndex={0}
              title={`Inquire about ${service.title}`}
            >
              <div className="service-glow" style={{ background: service.gradient }} />
              <div className="service-icon-wrap" style={{ background: service.gradient }}>
                <span className="service-icon">{service.icon}</span>
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <div className="service-tags">
                {service.tags.map((tag, idx) => (
                  <span key={idx} className="service-tag">{tag}</span>
                ))}
              </div>
              <div className="service-arrow">Inquire Now →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
